import { useState, useEffect } from 'react'
import { format, subDays, subMonths, parse } from 'date-fns'
import { zhCN } from 'date-fns/locale'
import { CalendarIcon, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Calendar } from '@/components/ui/calendar'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import type { DateRange } from 'react-day-picker'

interface DateRangePickerProps {
	value?: { start_date?: string; end_date?: string }
	onChange: (value: { start_date?: string; end_date?: string }) => void
	className?: string
}

type PresetKey = 'today' | '7days' | '30days' | '3months' | '6months' | '1year'

interface Preset {
	key: PresetKey
	label: string
	getRange: (today: Date) => { from: Date; to: Date }
}

const presets: Preset[] = [
	{ key: 'today', label: '今天', getRange: (today) => ({ from: today, to: today }) },
	{ key: '7days', label: '最近7天', getRange: (today) => ({ from: subDays(today, 7), to: today }) },
	{ key: '30days', label: '最近30天', getRange: (today) => ({ from: subDays(today, 30), to: today }) },
	{ key: '3months', label: '最近3个月', getRange: (today) => ({ from: subMonths(today, 3), to: today }) },
	{ key: '6months', label: '最近半年', getRange: (today) => ({ from: subMonths(today, 6), to: today }) },
	{ key: '1year', label: '最近1年', getRange: (today) => ({ from: subMonths(today, 12), to: today }) },
]

export function DateRangePicker({ value, onChange, className }: DateRangePickerProps) {
	const [isOpen, setIsOpen] = useState(false)
	const [selectedPreset, setSelectedPreset] = useState<PresetKey | null>(null)
	const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
		// 从外部 value 初始化内部状态
		if (value?.start_date) {
			const from = parse(value.start_date, 'yyyy-MM-dd', new Date())
			const to = value.end_date ? parse(value.end_date, 'yyyy-MM-dd', new Date()) : from
			return { from, to }
		}
		return undefined
	})

	// 同步外部 value 到内部状态
	useEffect(() => {
		if (value?.start_date) {
			const from = parse(value.start_date, 'yyyy-MM-dd', new Date())
			const to = value.end_date ? parse(value.end_date, 'yyyy-MM-dd', new Date()) : from
			setDateRange({ from, to })
		} else {
			setDateRange(undefined)
			setSelectedPreset(null)
		}
	}, [value?.start_date, value?.end_date])

	const handlePresetClick = (preset: PresetKey) => {
		setSelectedPreset(preset)
		setPendingFrom(null)
		const today = new Date()
		const presetConfig = presets.find((p) => p.key === preset)

		if (presetConfig) {
			const range = presetConfig.getRange(today)
			setDateRange(range)
			onChange({
				start_date: format(range.from, 'yyyy-MM-dd'),
				end_date: format(range.to, 'yyyy-MM-dd'),
			})
			setIsOpen(false)
		}
	}

	// 手动选区的第一击暂存起点（react-day-picker v9 空区间首击返回 {from:d, to:d}，
	// 无法凭 range 判断是否选完，需自行按点击次数驱动）
	const [pendingFrom, setPendingFrom] = useState<Date | null>(null)

	const handleDateSelect = (range: DateRange | undefined, triggerDate?: Date) => {
		setSelectedPreset(null) // 手动选择时清除预设选中状态

		if (!triggerDate) {
			setDateRange(range)
			setPendingFrom(null)
			return
		}

		// 第一击：仅记录起点，弹窗保持打开等待第二击
		if (!pendingFrom) {
			setPendingFrom(triggerDate)
			setDateRange({ from: triggerDate, to: undefined })
			return
		}

		// 第二击：正序成区间后提交并关闭
		const from = pendingFrom
		const to = triggerDate
		const [start, end] = from.getTime() <= to.getTime() ? [from, to] : [to, from]
		setPendingFrom(null)
		setDateRange({ from: start, to: end })
		onChange({
			start_date: format(start, 'yyyy-MM-dd'),
			end_date: format(end, 'yyyy-MM-dd'),
		})
		setIsOpen(false)
	}

	const displayText = () => {
		if (!value?.start_date) return '选择日期范围'
		if (value.start_date === value.end_date) return value.start_date
		return `${value.start_date} ~ ${value.end_date}`
	}

	const handleClear = () => {
		setDateRange(undefined)
		setSelectedPreset(null)
		setPendingFrom(null)
		onChange({})
		setIsOpen(false)
	}

	return (
		<Popover
			open={isOpen}
			onOpenChange={(open) => {
				// 关闭时清掉未完成的选区，下次重新从第一击开始
				if (!open) setPendingFrom(null)
				setIsOpen(open)
			}}
		>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					size="sm"
					className={cn(
						'justify-start text-left font-normal min-w-[180px] group',
						!value?.start_date && 'text-muted-foreground',
						className
					)}
				>
					<CalendarIcon className="mr-2 h-4 w-4" />
					<span className="flex-1">{displayText()}</span>
					{value?.start_date && (
						<span
							role="button"
							tabIndex={0}
							className="ml-1 opacity-50 hover:opacity-100"
							onClick={(e) => {
								e.stopPropagation()
								handleClear()
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' || e.key === ' ') {
									e.stopPropagation()
									handleClear()
								}
							}}
						>
							<X className="h-3 w-3" />
						</span>
					)}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="w-auto p-0" align="start">
				<div className="flex">
					{/* 快捷选项 - 左侧 */}
					<div className="flex flex-col gap-1 p-3 border-r bg-muted/30">
						{presets.map((preset) => (
							<Button
								key={preset.key}
								variant={selectedPreset === preset.key ? 'default' : 'ghost'}
								size="sm"
								className="justify-start"
								onClick={() => handlePresetClick(preset.key)}
							>
								{preset.label}
							</Button>
						))}
						<div className="my-2 border-t" />
						<Button
							variant="ghost"
							size="sm"
							className="justify-start text-muted-foreground hover:text-foreground"
							onClick={handleClear}
						>
							<X className="mr-1 h-3 w-3" />
							清除
						</Button>
					</div>
					{/* 日历选择器 - 右侧 */}
					<div className="relative">
						<Calendar
							initialFocus
							mode="range"
							defaultMonth={dateRange?.from}
							selected={dateRange}
							onSelect={handleDateSelect}
							numberOfMonths={2}
							locale={zhCN}
						/>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	)
}
